import type { SemesterOption } from 'src/modules/School/composables/useSchoolData';

/**
 * Semester date arithmetic, shared so the client agrees with the server.
 *
 * Semester columns are `date`-typed, so everything here compares plain
 * `YYYY-MM-DD` strings in LOCAL time — the same rule the backend applies in
 * `general/utils/current-semester.ts`. `is_active` is deliberately ignored on
 * both sides: it is a manual flag with no single-active guarantee.
 */

/** Local-timezone YYYY-MM-DD. */
export function todayLocalISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = `${now.getMonth() + 1}`.padStart(2, '0');
  const d = `${now.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The semester today falls inside. At most one can match (no-overlap rule). */
export function isCurrentSemester(
  semester: Pick<SemesterOption, 'start_date' | 'end_date'>,
  today = todayLocalISO(),
): boolean {
  return (
    (semester.start_date || '') <= today && (semester.end_date || '') >= today
  );
}

/** A semester that has not started yet. */
export function isUpcomingSemester(
  semester: Pick<SemesterOption, 'start_date'>,
  today = todayLocalISO(),
): boolean {
  return (semester.start_date || '') > today;
}

/** A semester whose end date has passed. */
export function hasSemesterEnded(
  semester: Pick<SemesterOption, 'end_date'>,
  today = todayLocalISO(),
): boolean {
  return !!semester.end_date && semester.end_date < today;
}
